import { AnalyticsPeriodService } from '@/utils/services/analytics/AnalyticsPeriodService';
import { TimeService } from '@/utils/services/TimeService';

import {
    AnalyticsData,
    AnalyticsDataRecord,
    AnalyticsParams
} from '@/utils/hooks/swr/useAnalyticsV2';

import { get } from '@/utils/client';

type FormatParamsForV2Options = {
    params: AnalyticsParams;
    keepComparisonPeriod?: boolean;
};

class AnalyticsDataService {
    /**
     * Format and clean params for Analytics V2 API.
     * Handles date formatting and conditional param removal based on album_id presence.
     */
    formatParamsForV2 = ({
        params,
        keepComparisonPeriod = false
    }: FormatParamsForV2Options): AnalyticsParams => {
        const formattedParams = { ...params } as AnalyticsParams;

        if (!keepComparisonPeriod) {
            delete formattedParams.comparison_period;
            delete formattedParams.period;
        }

        if (formattedParams.start) {
            formattedParams.start =
                TimeService.formatDateForAnalyticsV2(formattedParams.start) ??
                formattedParams.start;
        }

        if (formattedParams.end) {
            formattedParams.end =
                TimeService.formatDateForAnalyticsV2(formattedParams.end, true) ??
                formattedParams.end;
        }

        if (formattedParams.album_id) {
            delete formattedParams.period;
            delete formattedParams.comparison_period;
            delete formattedParams.start;
            delete formattedParams.end;
        } else {
            delete formattedParams.album_id;
        }

        return formattedParams;
    };

    /**
     * Fetch comparison data and merge with current data.
     * Returns current data as-is if no comparison is requested.
     */
    fetchAndMergeComparison = async (
        url: string,
        apiParams: AnalyticsParams,
        requestParams: AnalyticsParams,
        currentData: AnalyticsDataRecord
    ): Promise<AnalyticsDataRecord> => {
        if (!requestParams.comparison_period) {
            return currentData;
        }

        try {
            const { startComparison, endComparison } =
                AnalyticsPeriodService.calculateComparisonPeriod({
                    start: requestParams.start,
                    end: requestParams.end,
                    compareTo: requestParams.comparison_period,
                    period: requestParams.period,
                    timezone: 'UTC'
                });

            const comparisonParams = {
                ...apiParams,
                start: startComparison,
                end: endComparison
            };

            let comparisonData;
            if (requestParams?.fields?.[0] && Array.isArray(requestParams?.fields?.[0])) {
                const comparisonResponses = await Promise.all(
                    (requestParams.fields as string[][]).map((fields) =>
                        get(url, { ...comparisonParams, fields }).then((res) => res.data)
                    )
                );
                comparisonData = comparisonResponses.reduce(
                    (result, current) => ({ ...result, ...current }),
                    {}
                );
            } else {
                const comparisonResponse = await get(url, comparisonParams);
                comparisonData = comparisonResponse.data;
            }

            const normalizedComparisonData = this.normalizeV2AnalyticsData(comparisonData);

            return this.mergeAnalyticsWithComparison(currentData, normalizedComparisonData);
        } catch (error) {
            console.warn(
                '[AnalyticsDataService]: Failed to fetch comparison data, returning current data only',
                error
            );
            return currentData;
        }
    };

    /**
     * Fetcher for Analytics V2 API.
     * Handles both single and multiple field arrays, normalizes data, and merges comparison if needed.
     */
    fetchAnalyticsV2 = async (
        url: string,
        requestParams: AnalyticsParams
    ): Promise<AnalyticsData> => {
        const apiParams = this.formatParamsForV2({ params: requestParams });

        let currentData;
        if (requestParams?.fields?.[0] && Array.isArray(requestParams?.fields?.[0])) {
            const responses = await Promise.all(
                (requestParams.fields as string[][]).map((fields) =>
                    get(url, { ...apiParams, fields }).then((res) => res.data)
                )
            );
            currentData = responses.reduce((result, current) => ({ ...result, ...current }), {});
        } else {
            const response = await get(url, apiParams);
            currentData = response.data;
        }

        const normalizedCurrentData = this.normalizeV2AnalyticsData(currentData);

        const finalData = await this.fetchAndMergeComparison(
            url,
            apiParams,
            requestParams,
            normalizedCurrentData
        );

        return { analytics: finalData };
    };

    /**
     * Normalizes Industry Benchmark analytics response by adding graphData from value object.
     * Specifically for pie charts where `value` is an object (distribution data).
     * Creates `graphData: [value]` to maintain compatibility with pie chart components.
     *
     * Example:
     * Input:  { engagement_rate: { value: { avg: 3.5, median: 3.2 } } }
     * Output: { engagement_rate: { value: { avg: 3.5, median: 3.2 }, graphData: [{ avg: 3.5, median: 3.2 }] } }
     */
    normalizeIndustryBenchmarkData = (analytics) => {
        if (!analytics) {
            return analytics;
        }

        const transformed = {};

        Object.keys(analytics).forEach((fieldKey) => {
            const fieldData = analytics[fieldKey];
            if (!fieldData) {
                transformed[fieldKey] = fieldData;
                return;
            }

            const isValueObject =
                typeof fieldData.value === 'object' &&
                fieldData.value !== null &&
                !Array.isArray(fieldData.value);

            transformed[fieldKey] = isValueObject
                ? { ...fieldData, graphData: [fieldData.value] }
                : fieldData;
        });

        return transformed;
    };

    /**
     * Normalizes V2 analytics response for time series data.
     * When `value` is an array (time series data), converts it to `graphData` format.
     * This is used for standard analytics endpoints that return time series.
     *
     * Example:
     * Input:  { followers_count: { value: [100, 200, 300] } }
     * Output: { followers_count: { graphData: [100, 200, 300] } }
     */
    normalizeV2AnalyticsData = (data) => {
        if (!data) return data;

        const normalizedAnalytics = {};

        Object.keys(data).forEach((key) => {
            const field = data[key];
            if (!field) {
                normalizedAnalytics[key] = field;
                return;
            }

            // Convert value arrays to graphData to match V1 logic
            if (Array.isArray(field.value)) {
                normalizedAnalytics[key] = {
                    graphData: field.value
                };
            } else {
                normalizedAnalytics[key] = field;
            }
        });

        return normalizedAnalytics;
    };

    /**
     * Merge current analytics data with comparison period data
     * Adds 'previous' and 'evolution' fields to each KPI
     * @param currentData - Analytics data for the current period
     * @param comparisonData - Analytics data for the comparison period
     * @returns Merged analytics data with comparison metrics
     */
    mergeAnalyticsWithComparison = (
        currentData: AnalyticsDataRecord,
        comparisonData: AnalyticsDataRecord
    ): AnalyticsDataRecord => {
        if (!currentData) return currentData;
        if (!comparisonData) return currentData;

        const mergedData: AnalyticsDataRecord = { ...currentData };

        Object.keys(currentData).forEach((kpiKey) => {
            const currentKPI = currentData[kpiKey];
            const comparisonKPI = comparisonData[kpiKey];

            // Skip if this is not a KPI with a value (might be graphData only)
            if (typeof currentKPI?.value !== 'number') {
                return;
            }

            // Add comparison data if available
            if (comparisonKPI && typeof comparisonKPI.value === 'number') {
                mergedData[kpiKey] = {
                    ...currentKPI,
                    previous: comparisonKPI.value,
                    evolution: AnalyticsPeriodService.calculateEvolution(
                        currentKPI.value,
                        comparisonKPI.value
                    )
                };
            } else {
                // No comparison data available, set to null (not undefined)
                mergedData[kpiKey] = {
                    ...currentKPI,
                    previous: null,
                    evolution: null
                };
            }
        });

        return mergedData;
    };
}

const analyticsDataService = new AnalyticsDataService();

export { analyticsDataService as AnalyticsDataService };
